import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { baseURL, headers } from '@/v2/constants/requests'

const MONTH_QUERY_KEY = 'SettingsScheduleMonth'

export const useMonthSchedule = (year, month) => {
  const queryKey = [MONTH_QUERY_KEY, year, month]

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/schedules/month/${year}/${month}`,
        { method: 'GET', headers }
      )
      if (!res.ok) {
        throw new Error("We couldn't load the month schedule")
      }
      return res.json()
    },
    enabled: !!year && !!month,
  })

  return { monthData: data, isLoading, isError }
}

export const useDaySchedule = dateString => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['SettingsScheduleDay', dateString],
    queryFn: async () => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/schedules/day/${dateString}`,
        { method: 'GET', headers }
      )
      if (!res.ok) {
        throw new Error("We couldn't load the day schedule")
      }
      return res.json()
    },
    enabled: !!dateString,
  })

  return { dayData: data, isLoading, isError, refetch }
}

export const useUpdateDaySchedule = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: updateDaySchedule, ...rest } = useMutation({
    mutationFn: async ({ dateString, daySchedule }) => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/schedules/day/${dateString}`,
        {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ daySchedule }),
        }
      )
      if (!res.ok) {
        const error = await res.json()
        throw new Error(
          error.errors
            ? JSON.stringify(error.errors)
            : 'Could not update day schedule'
        )
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MONTH_QUERY_KEY] })
    },
  })

  return { updateDaySchedule, ...rest }
}

export const useApplyDefaults = () => {
  const queryClient = useQueryClient()

  const { mutateAsync: applyDefaults, ...rest } = useMutation({
    mutationFn: async dateString => {
      const res = await fetch(
        `${baseURL}/v2/rest/settings/schedules/day/${dateString}/apply_defaults`,
        { method: 'POST', headers }
      )
      if (!res.ok) {
        throw new Error('Failed to apply defaults')
      }
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MONTH_QUERY_KEY] })
    },
  })

  return { applyDefaults, ...rest }
}
